<script setup>
  import LegalPageLayout from '@/Layouts/LegalPageLayout.vue';
  import LegalList from '@/Components/LegalList.vue';
  import LegalOrderedList from '@/Components/LegalOrderedList.vue';
  import LegalSection from '@/Components/LegalSection.vue';
  import { Link } from '@inertiajs/vue3';
  import { Head } from '@inertiajs/vue3';

  defineProps({
    title: {
      type: String,
      default: 'Privacy Policy'
    }
  });
</script>

<template>
  <Head :title="title" />
  <LegalPageLayout :title="title">
    <p>Last modified: October 1, 2024</p>

    <LegalSection title="Introduction">
      <p>
        MMT Dev LLC (the
        <span class="font-bold">“Company”</span>
        or
        <span class="font-bold">“We”</span>
        ) respects your privacy and is committed to protecting it through our
        compliance with this policy.
      </p>
      <p>
        This policy describes the types of information we may collect from you
        or that you may provide when you visit the website
        <Link
          href="http://www.spicycollab.com"
          class="font-semibold text-teal-600 hover:underline"
        >
          www.spicycollab.com
        </Link>
        (our
        <span class="font-bold">“Website”</span>
        ) and our practices for collecting, using, maintaining, protecting, and
        disclosing that information.
      </p>
    </LegalSection>

    <LegalSection title="This Privacy Policy applies to Information We Collect">
      <LegalList
        :items="[
          'We collect the following personal information when you use our website:',
          'Name',
          'Email address',
          'Profile Details (e.g., interests, skills, etc.)',
          'Telephone number',
          'Address',
          'IP address',
          'Date of birth',
          'Gender',
          'Payment information (e.g., credit card number, billing address)',
          'Usage Data'
        ]"
      />
      <p>
        Please read this policy carefully to understand our policies and
        practices regarding your information and how we will treat it. If you
        disagree with our policies and practices, your choice is not to use our
        Website. By accessing or using this Website, you agree to this privacy
        policy. We may change this policy on one or more occasions (see
        <Link
          href="/docs/privacy-policy"
          class="font-semibold text-teal-600 hover:underline"
        >
          Changes to Our Privacy Policy
        </Link>
        ). Your continued use of this Website after we make changes is deemed to
        be acceptance of those changes, so please check the policy periodically
        for updates.
      </p>
    </LegalSection>

    <LegalSection
      title="Individuals Under the Age of 18
"
    >
      <p>
        Our Website is not intended for individuals under 18 years of age. No
        one under 18 may provide any information to or on the Website. We do not
        knowingly collect personal information from individuals under 18. If you
        are under 18, do not use or provide any information on this Website or
        through any of its features, register on the Website, make any purchases
        through the Website, use any interactive or public comment features of
        this Website, or provide any information about yourself to us, including
        your name, address, telephone number, email address, or any username you
        may use. If we learn we have collected or received personal information
        from an individual under 18, we will delete that information. If you
        believe we might have any information from or about an individual under
        18, please contact us at
        <a
          :href="'mailto:privacy@spicycollab.com'"
          class="font-semibold text-malibu hover:underline"
        >
          privacy@spicycollab.com
        </a>
      </p>
    </LegalSection>

    <LegalSection
      title="Information We Collect About You and How We Collect It"
    >
      <p>
        We collect several types of information from and about users of our
        Website, including:
      </p>
      <ol class="list-disc pl-6">
        <li>
          <span class="font-semibold">Personal information,</span>
          that we can reasonably use to directly or indirectly identify you,
          including your name, email address, government-issued picture
          identification, Internet protocol (IP) address used to connect your
          computer to the Internet, username or other similar identifier, or any
          other identifier by which you may be contacted online or offline
          (“personal information”).
        </li>
        <li>
          <span class="font-semibold">Non-personal information,</span>
          that does not directly or indirectly reveal your identity or directly
          relate to an identifiable individual, including demographic
          information, or statistical or aggregated information. Statistical or
          aggregated data does not directly identify a specific person, but we
          may derive nonpersonal statistical or aggregated data from personal
          information. For example, we may aggregate personal information to
          calculate the percentage of users accessing a specific Website
          feature.
        </li>
        <li>
          <span class="font-semibold">Technical information,</span>
          including your login information, browser type and version, time zone
          setting, browser plug-in types and versions, operating system and
          platform, or information about your internet connection, the equipment
          you use to access our Website, and usage details.
        </li>
        <li>
          <span class="font-semibold">
            Nonpersonal details about your Website interactions,
          </span>
          including the full Uniform Resource Locators (URLs), clickstream to,
          through, and from our Website (including date and time), products you
          viewed or searched for, page response times, download errors, length
          of visits to certain pages, page interaction information (including
          scrolling, clicks, and mouse-overs), or methods used to browse away
          from the page.
        </li>
      </ol>
      <p class="mt-4">We collect this information:</p>
      <ol class="mt-4 list-disc pl-6">
        <li>Directly from you when you provide it to us.</li>
        <li>
          Automatically as you navigate through the Website. Information
          collected automatically might include usage details, IP addresses, and
          information collected through cookies, web beacons, and other tracking
          technologies.
        </li>
        <li>From nonparties, for example, our business partners.</li>
      </ol>
    </LegalSection>

    <LegalSection title="Information You Provide to Us">
      <p>The information we collect on or through our Website may include:</p>
      <LegalList
        :items="[
          'Information that you provide by filling in forms on our Website. This includes information provided when registering to use our Website, subscribing to our service, posting material, or requesting further services. We may also ask you for information when you report a problem with our Website.',
          'Records and copies of your correspondence (including email addresses), if you contact us.',
          'Your responses to surveys that we might ask you to complete for research purposes.',
          'Details of transactions you carry out through our Website and of the fulfillment of your orders. You will be required to provide financial information before placing an order through our Website. We do not collect or store your payment card details. That information is provided directly to our payment processor, whose use of your personal information is governed by its privacy policy.',
          'Your search queries on the Website.'
        ]"
      />
      <p>
        You also may provide information to be published or displayed (“posted”)
        on public areas of the Website or transmitted to other users of the
        Website or nonparties (collectively, “User Contributions”). Your User
        Contributions are posted on and transmitted to others at your own risk.
        Although we limit access to certain pages, please be aware that no
        security measures are perfect or impenetrable. Additionally, we cannot
        control the actions of other users of the Website with whom you may
        choose to share your User Contributions. Therefore, we cannot and do not
        guarantee that unauthorized persons will not view your User
        Contributions.
      </p>
    </LegalSection>

    <LegalSection
      title="Information We Collect Through Automatic Data Collection Technologies"
    >
      <p>
        As you navigate through and interact with our Website, we may use
        automatic data collection technologies to collect certain information
        about your equipment, browsing actions, and patterns, including:
      </p>
      <LegalList
        :items="[
          'Details of your visits to our Website, including traffic data, location data, logs, and other communication data and the resources that you access and use on the Website.',
          'Information about your computer and internet connection, including your IP address, operating system, and browser type.'
        ]"
      />
      <p>
        Please note that we do not recognize or respond to any do-not-track
        signals (DNT). For more information about DNT, visit
        <Link
          href="https://www.allaboutdnt.com"
          class="font-semibold text-malibu hover:underline"
        >
          www.allaboutdnt.com
        </Link>
        .
      </p>
      <p>
        The information we collect automatically may include personal
        information. It helps us to improve our Website and to deliver a better
        and more personalized service, including by enabling us to:
      </p>
      <LegalList
        :items="[
          'Estimate our audience size and usage patterns.',
          'Store information about your preferences, allowing us to customize our Website according to your individual interests.',
          'Speed up your searches.',
          'Recognize you when you return to our Website.'
        ]"
      />
      <p>
        The technologies we use for this automatic data collection may include:
      </p>
      <ol>
        <li>
          <span class="font-semibold">Cookies (or browser cookies).</span>
          A cookie is a small file placed on your computer’s hard drive. You may
          refuse to accept browser cookies by activating the appropriate setting
          on your browser. However, if you select this setting you may be unable
          to access certain parts of our Website. Unless you have adjusted your
          browser setting so that it will refuse cookies, our system will issue
          cookies when you direct your browser to our Website. For information
          about managing your privacy and security settings for cookies, see
          Choices About How We Use and Disclose Your Information.
        </li>
        <li>
          <span class="font-semibold">Web Beacons.</span>
          Pages of our Website and our emails may contain small electronic files
          known as web beacons (also referred to as clear gifs, pixel tags, and
          single-pixel gifs) that permit the Company, for example, to count
          users who have visited those pages or opened an email and for other
          related website statistics (for example, recording the popularity of
          certain website content and verifying system and server integrity).
        </li>
      </ol>
    </LegalSection>

    <LegalSection title="How We Use Your Information">
      <p>
        We use information that we collect about you or that you provide to us,
        including any personal information:
      </p>
      <LegalList
        :items="[
          'To present our Website and its contents to you.',
          'To provide you with information, products, or services that you request from us.',
          'To fulfill any other purpose for which you provide it.',
          'To provide you with notices about your account, including expiration and renewal notices.',
          'To carry out our obligations and enforce our rights arising from any contracts entered into between you and us, including for billing and collection.',
          'To notify you about changes to our Website or any products or services we offer or provide through it.',
          'To allow you to participate in interactive features on our Website.',
          'To verify your identity and age.',
          'In any other way we may describe when you provide the information.',
          'For any other purpose with your consent.'
        ]"
      />
      <p>
        We may also use your information to contact you about our goods and
        services that may be of interest to you. If you do not want us to use
        your information this way, please click unsubscribe at the bottom of the
        email. For more information, see
        <!-- TODO: get proper link -->
        <Link href="#" class="font-semibold text-malibu hover:underline">
          Choices About How We Use and Disclose Your Information
        </Link>
        .
      </p>
    </LegalSection>

    <LegalSection title="Disclosure of Your Information">
      <p>
        We may disclose aggregated information about our users, and information
        that does not identify any individual, without restriction.
      </p>
      <p>
        We may disclose personal information that we collect or you provide as
        described in this privacy policy:
      </p>
      <ol>
        <li>To our subsidiaries and affiliates.</li>
        <li>
          To contractors, service providers, and other nonparties we use to
          support our business and who are bound by contractual obligations to
          keep personal information confidential and use it only for the
          purposes for which we disclose it to them. For example, we use Google
          Analytics to help us understand how our users use our Website (you can
          read more about how Google uses your personal information here:
          <Link
            href="https://policies.google.com/privacy"
            class="font-semibold text-malibu hover:underline"
          >
            https://policies.google.com/privacy
          </Link>
          ).
        </li>
        <li>
          To a buyer or other successor if a merger, divestiture, restructuring,
          reorganization, dissolution, or other sale or transfer of some or all
          the Company’s assets, whether as a going concern or as part of
          bankruptcy, liquidation, or similar proceeding, in which personal
          information held by the Company about our Website users is among the
          assets transferred.
        </li>
        <li>To fulfill the purpose for which you provide it.</li>
        <li>
          For any other purpose disclosed by us when you provide the
          information.
        </li>
        <li>With your consent.</li>
      </ol>
      <p>We may also disclose your personal information:</p>
      <ol>
        <li>
          To comply with any court order, law, or legal process, including
          responding to any government or regulatory request.
        </li>
        <li>
          To enforce or apply our
          <Link href="/terms" class="font-semibold text-malibu hover:underline">
            Terms of Service
          </Link>
          and other agreements, including for billing and collection purposes.
        </li>
        <li>
          If we believe disclosure is necessary or appropriate to protect the
          rights, property, or safety of the Company, our customers, or others.
          This includes exchanging information with other companies and
          organizations for fraud protection and credit risk reduction purposes.
        </li>
      </ol>
    </LegalSection>

    <LegalSection title="Consent to Personal Information Transfer">
      <p>
        We are based in the United States of America. We may process, store, and
        transfer the personal information we collect, in and to a country
        outside your own, with different privacy laws that might not be as
        comprehensive as your own.
      </p>
      <p>
        By submitting your personal information or engaging with our Website,
        you hereby consent to this transfer, storing, or processing, including
        transferring your information across international boundaries to
        jurisdictions anywhere in the world as permitted by law.
      </p>
      <p>
        If you are a Canadian resident or otherwise located in Canada, please
        note that personal information transfers outside of Canada might result
        in your information becoming accessible to foreign jurisdiction’s law
        enforcement or other authorities.
      </p>
      <p>
        If you are located in the European Economic Area (EEA), Switzerland, or
        the United Kingdom (UK), please note that your information will be
        transferred outside of those areas, including to the United States.
        Nevertheless, whenever we transfer your personal information out of the
        EEA, Switzerland, or UK, we will use reasonable efforts to ensure a
        similar degree of protection is afforded to it by ensuring that the
        recipient nonparty agrees to contractual clauses or other appropriate
        safeguards.
      </p>
    </LegalSection>

    <LegalSection
      title="Choices About How We Use and Disclose Your Information"
    >
      <p>
        We strive to provide you with choices regarding the personal information
        you provide to us. We have created mechanisms to provide you with the
        following control over your information:
      </p>
      <ol>
        <li>
          <span class="font-semibold">
            Tracking Technologies and Advertising.
          </span>
          You can set your browser to refuse all or some browser cookies or to
          alert you when cookies are being sent. If you disable or refuse
          cookies, please note that some parts of our Website might become
          inaccessible or not function properly. You can opt out of Google
          Analytics here:
          <Link
            href="https://tools.google.com/dlpage/gaoptout"
            class="font-semibold text-malibu hover:underline"
          >
            Optout of Google Analytics
          </Link>
          .
        </li>
        <li>
          <span class="font-semibold">
            Promotional Offers from the Company.
          </span>
          If you do not wish to have your email address used by the Company to
          promote our own products or services, you can opt-out by sending us an
          email stating your request to
          <a
            mailto="privacy@spicycollab.com"
            class="font-semibold text-malibu hover:underline"
          >
            privacy@spicycollab.com
          </a>
          . If we have sent you a promotional email, you may send us a return
          email asking to be omitted from future email distributions or click
          the unsubscribe button at the bottom of the email. This opt-out does
          not apply to information provided to the Company as a result of a
          subscription purchase, product service experience, or other
          transactions.
        </li>
      </ol>
      <p class="mt-4">
        Residents of certain jurisdictions, including California, Colorado,
        Connecticut, Delaware, Florida, Indiana, Iowa, Montana, Oregon,
        Tennessee, Texas, Utah, Virginia, the European Economic Area, and the
        United Kingdom might have additional personal information rights and
        choices. Please see Your State Privacy Rights and Your EEA/UK Privacy
        Rights for more information.
      </p>
    </LegalSection>

    <LegalSection title="Accessing and Correcting Your Information">
      <p>
        It is important that the personal information we hold about you is
        accurate and current. Please keep us informed if your personal
        information changes.
      </p>
      <p>
        You can review and change your personal information by logging into the
        Website and visiting your account profile page.
      </p>
      <p>
        You may also send us an email at
        <a
          mailto="privacy@spicycollab.com"
          class="font-semibold text-malibu hover:underline"
        >
          privacy@spicycollab.com
        </a>
        to request access to, correct, or delete any personal information you
        have provided us. We cannot delete your personal information except by
        also deleting your user account. We might not accommodate a request to
        change information if we believe the change would violate any law or
        legal requirement or cause the information to be incorrect.
      </p>
      <p>
        We may request specific information from you to help us confirm your
        identity and your right to access, and to provide you with the personal
        information we hold about you or make your requested changes. Law may
        allow or require us to refuse to provide you with access to some or all
        the personal information we hold about you, or we may have destroyed,
        erased, or made your personal information anonymous per our record
        retention obligations and practices. If we cannot provide you with
        access to your personal information, we will inform you of the reasons
        why, subject to any legal or regulatory restrictions.
      </p>
      <p>
        If you delete your User Contributions from the Website, copies of your
        User Contributions might remain viewable in cached and archived pages or
        might have been copied or stored by other Website users. Proper access
        and use of information provided on the Website, including User
        Contributions, is governed by our
        <Link href="/terms" class="font-semibold text-malibu hover:underline">
          Terms of Service
        </Link>
        .
      </p>
      <!-- TODO: get proper link -->
      <p>
        Residents of certain jurisdictions, including California, Colorado,
        Connecticut, Delaware, Florida, Indiana, Iowa, Montana, Oregon,
        Tennessee, Texas, Utah, Virginia, the European Economic Area, and the
        United Kingdom might have additional personal information rights and
        choices. Please see
        <Link href="#" class="font-semibold text-malibu hover:underline">
          Your State Privacy Rights
        </Link>
        and
        <Link href="#" class="font-semibold text-malibu hover:underline">
          Your EEA/UK Privacy Rights
        </Link>
        for more information.
      </p>
    </LegalSection>

    <LegalSection title="Your State Privacy Rights">
      <p>
        State consumer privacy laws may provide their residents with additional
        rights regarding our use of their personal information.
      </p>
      <p>
        California, Colorado, Connecticut, Delaware, Florida, Indiana, Iowa,
        Montana, Oregon, Tennessee, Texas, Utah, and Virginia provide (now or in
        the future) their state residents with rights to:
      </p>
      <ol>
        <li>Confirm whether we process their personal information.</li>
        <li>Access and delete certain personal information.</li>
        <li>
          Correct inaccuracies in their personal information, taking into
          account the information’s nature processing purpose (excluding Iowa
          and Utah).
        </li>
        <li>Data portability.</li>
        <li>
          Opt-out of personal data processing for:
          <ol class="ml-6 !list-[circle]">
            <li>targeted advertising (excluding Iowa);</li>
            <li>sales; or</li>
            <li>
              profiling in furtherance of decisions that produce legal or
              similarly significant effects (excluding Iowa and Utah).
            </li>
          </ol>
        </li>
        <li>
          Either limit (opt-out of) or require consent to process sensitive
          personal data.
        </li>
      </ol>
      <p>
        The exact scope of these rights might vary by state. To exercise any of
        these rights, please email us at
        <a
          mailto="privacy@spicycollab.com"
          class="font-semibold text-malibu hover:underline"
        >
          privacy@spicycollab.com
        </a>
        . To appeal a decision regarding a consumer rights request, please
        provide a detailed explanation of why you believe the decision was
        incorrect or unsatisfactory and any additional information or evidence
        that supports your appeal via email to
        <a
          mailto="privacy@spicycollab.com"
          class="font-semibold text-malibu hover:underline"
        >
          privacy@spicycollab.com
        </a>
        . On receipt of your appeal, we will acknowledge its receipt within ten
        business days. The appeal will be reviewed by a senior member of our
        data protection or compliance team who was not involved in the initial
        decision. We may contact you for further information or clarification if
        necessary. We will provide a written response to your appeal within 30
        days of receipt. If additional time is required to review your appeal,
        we will notify you of the extension and the reasons for it. If your
        appeal is upheld, we will take the necessary steps to rectify the issue
        and inform you of the actions taken. If your appeal is denied, we will
        provide a detailed explanation of the reasons for the denial and inform
        you of any further options available, such as contacting the relevant
        state authorities or data protection agencies.
      </p>
      <p>
        Nevada provides its residents with a limited right to opt-out of certain
        personal information sales. Residents who wish to exercise this sale
        opt-out rights may submit a request to this designated address:
        <a
          mailto="privacy@spicycollab.com"
          class="font-semibold text-malibu hover:underline"
        >
          privacy@spicycollab.com
        </a>
        . However, please know we do not currently sell data triggering that
        statute’s opt-out requirements.
      </p>
    </LegalSection>

    <LegalSection title="Your EEA/UK Privacy Rights">
      <p>If you live in the EEA or the UK, the following terms apply to you.</p>
    </LegalSection>

    <LegalSection title="Legal Basis for Processing">
      <p>
        We may process your personal data because you have given us permission
        to do so (for example, by sending data through our Website’s contact or
        signup forms), because the processing is in our legitimate interests and
        it is not overridden by your rights, or because we need to process your
        personal data to perform a contract with you or comply with the law.
      </p>
    </LegalSection>

    <LegalSection title="Your Legal Rights">
      Under certain circumstances, you have rights under data protection laws
      concerning your personal data. Your rights may include the following:
      <ol>
        <li>
          <span class="font-semibold">Request access</span>
          to your personal data (commonly known as a “data subject access
          request”). This enables you to receive a copy of the personal data we
          hold about you and to check that we are lawfully processing it.
        </li>
        <li>
          <span class="font-semibold">Request correction</span>
          of the personal data that we hold about you. This enables you to have
          any incomplete or inaccurate data we hold about you corrected, though
          we may need to verify the accuracy of the new data you provide to us.
        </li>
        <li>
          <span class="font-semibold">Request erasure</span>
          of your personal data. This enables you to ask us to delete or remove
          personal data where there is no good reason for us to continue to
          process it. You also have the right to ask us to delete or remove your
          personal data where you have successfully exercised your right to
          object to processing (see below), where we may have processed your
          information unlawfully, or where we are required to erase your
          personal data to comply with local law. Note, however, that we might
          not always be able to comply with your erasure request for specific
          legal reasons that will be notified to you, if applicable, at the time
          of your request.
        </li>
        <li>
          <span class="font-semibold">Object to processing</span>
          of your personal data where we are relying on a legitimate interest
          (or those of a third party) and there is something about your
          particular situation that makes you want to object to processing on
          this ground as you feel it impacts on your fundamental rights and
          freedoms. You also have the right to object where we are processing
          your personal data for direct marketing purposes. In some cases, we
          may demonstrate that we have compelling legitimate grounds to process
          your information that override your rights and freedoms.
        </li>
        <ol class="ml-6 !list-[circle]">
          <li>
            <span class="font-semibold">Request restriction of processing</span>
            your personal data. This enables you to ask us to suspend the
            processing of your personal data in the following scenarios: (1) if
            you want us to establish the data’s accuracy; (2) where our use of
            the data is unlawful, but you do not want us to erase it; (3) where
            you need us to hold the data even if we no longer require it as you
            need it to establish, exercise, or defend legal claims; or (4) you
            have objected to our use of your data, but we need to verify whether
            we have overriding legitimate grounds to use it.
          </li>
        </ol>
        <li>
          <span class="font-semibold">Request the transfer</span>
          of your personal data to you or a third party. We will provide to you,
          or a third party you have chosen, your personal data in a structured,
          commonly used, machine-readable format. Note that this right only
          applies to automated information that you initially provided consent
          for us to use or where we used the information to perform a contract
          with you.
        </li>
        <li>
          <span class="font-semibold">Withdraw consent at any time</span>
          where we are relying on consent to process your personal data.
          However, this will not affect the lawfulness of any processing carried
          out before you withdraw your consent. If you withdraw your consent, we
          might not be able to provide certain products or services to you. We
          will advise you if this is the case at the time you withdraw your
          consent.
        </li>
      </ol>
      <p>
        If you wish to exercise any of the rights set out above, please contact
        us at privacy@spicycollab.com.
      </p>
      <p>
        You will not have to pay a fee to access your personal data (or to
        exercise any of the other rights). However, we may charge a reasonable
        fee if your request is clearly unfounded, repetitive, or excessive.
        Alternatively, we may refuse to honor your request in these
        circumstances.
      </p>
      <p>
        We may need to request specific information from you to help us confirm
        your identity and ensure your right to access your personal data (or to
        exercise any of your other rights). This is a security measure to ensure
        that personal data is not disclosed to any person who has no right to
        receive it. We may also contact you to ask you for further information
        regarding your request to speed up our response.
      </p>
      <p>
        We try to respond to all legitimate requests within one month.
        Occasionally it may take us longer than a month if your request is
        particularly complex or you have made several requests. In this case, we
        will let you know and keep you updated.
      </p>
    </LegalSection>

    <LegalSection title="Data Security">
      <p>
        We have implemented measures designed to secure your personal
        information from accidental loss and from unauthorized access, use,
        alteration, and disclosure. We store all information you provide to us
        on our secure servers behind firewalls. Our payment processor encrypts
        any payment transactions using SSL technology.
      </p>
      <p>
        The safety and security of your information also depends on you. Where
        we have given you (or where you have chosen) a password for access to
        certain parts of our Website, you are responsible for keeping this
        password confidential. We ask you not to share your password with
        anyone. We urge you to be careful about giving out information in public
        areas of the Website. The information you share in public areas may be
        viewed by any user of the Website.
      </p>
      <p>
        Unfortunately, the transmission of information through the internet is
        not completely secure. Although we do our best to protect your personal
        information, we cannot guarantee the security of your personal
        information transmitted to our Website. Any transmission of personal
        information is at your own risk. We are not responsible for
        circumvention of any privacy settings or security measures contained on
        the Website.
      </p>
    </LegalSection>

    <LegalSection title="Data Retention">
      <p>
        Except as otherwise permitted or required by law or regulation, we will
        only retain your personal information for as long as necessary to
        fulfill the purposes we collected it for, including for the purposes of
        satisfying any legal, accounting, or reporting requirements. Under some
        circumstances, we may anonymize your personal information so that it can
        no longer be associated with you. We may use that anonymous and
        de-identified data for any legitimate business purpose without further
        notice to you or your consent.
      </p>
      <p>
        We adhere to the principle of data minimization, collecting only the
        information necessary to fulfill the purposes outlined in this policy.
        We periodically review the data we hold and ensure that unnecessary data
        is securely deleted or anonymized.
      </p>
    </LegalSection>

    <LegalSection title="Withdrawing Your Consent">
      <p>
        Where you have provided your consent to the collection, use, and
        transfer of your personal information, you may have the legal right to
        withdraw your consent under certain circumstances. To withdraw your
        consent, if applicable, contact us at privacy@spicycollab.com. Please note
        that if you withdraw your consent, we might not be able to provide you
        with a particular product or service. We will explain the impact to you
        at the time to help you with your decision.
      </p>
    </LegalSection>

    <LegalSection title="Changes to Our Privacy Policy">
      <p>
        Our policy is to post any changes we make to our privacy policy on this
        page. If we make material changes to how we treat our users’ personal
        information, we will notify you by email to the email address specified
        in your account or through a notice on the Website home page. The date
        the privacy policy was last revised is identified at the top of the
        page. You are responsible for ensuring we have an up-to-date, active,
        and deliverable email address for you and for periodically visiting our
        Website and this privacy policy to check for any changes.
      </p>
    </LegalSection>

    <LegalSection title="Contact Information">
      <p>
        To ask questions or comment about this privacy policy and our privacy
        practices, contact us at
        <a
          mailto="privacy@spicycollab.com"
          class="font-semibold text-teal-600 hover:underline"
        >
          privacy@spicycollab.com
        </a>
        .
      </p>
    </LegalSection>
  </LegalPageLayout>
</template>

<style scoped>
  ol {
    @apply list-inside list-disc pl-6;
  }
  p {
    @apply mt-4;
  }
</style>
